/* Sohi — product detail page (PDP). Shared, byte-identical between the
   static demo and the Shopify theme: it reads only what's already in the
   DOM — a <script type="application/json" data-product-variants> payload
   and a set of .pill pack-size radios (name="pack", each carrying
   data-variant-id) — and never touches window.SohiCatalog or a ?flavour=
   query param. Whoever produced that DOM (hydration in
   sohi-product-data.js for the static demo, sections/sohi-product.liquid
   on Shopify) is a solved problem by the time this file runs — do not
   import the catalog file here, and do not branch on which deliverable
   this is running in. */
(function () {
  "use strict";

  function money(cents) {
    var sign = cents < 0 ? "-" : "";
    return sign + "$" + (Math.abs(cents) / 100).toFixed(2);
  }

  function clampQty(n) {
    if (isNaN(n)) n = 1;
    return Math.min(99, Math.max(1, n));
  }

  function readVariants(root) {
    var el = root.querySelector("[data-product-variants]");
    if (!el) return [];
    try {
      var data = JSON.parse(el.textContent);
      return Array.isArray(data) ? data : [];
    } catch (e) {
      return [];
    }
  }

  function findVariant(variants, id) {
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].id === id) return variants[i];
    }
    return null;
  }

  function checkedPack(root) {
    return root.querySelector('[name="pack"]:checked') || root.querySelector('[name="pack"]');
  }

  // ---- pack upgrades ------------------------------------------------------
  // How many cups one unit of a variant holds, read off its own option label
  // ("4-pack" → 4, "Single cup" → 1), so this works the same whether the
  // labels came from the demo catalog or from a merchant's variant names.
  function unitsOf(variant) {
    var match = /(\d+)/.exec(variant && variant.option);
    return match ? parseInt(match[1], 10) : 1;
  }

  // Buying 4 single cups costs more than one 4-pack, so once the quantity
  // adds up to a pack that is both cheaper per cup and an exact fit, swap to
  // it. "Exact fit" is what keeps this predictable: 4 cups becomes the
  // 4-pack, 8 becomes the 8-pack, and anything that doesn't divide evenly
  // (5, 7, or 16 once you're already on the biggest pack) is left alone.
  function betterPack(variants, current, cups) {
    var currentUnits = unitsOf(current);
    var best = null;

    variants.forEach(function (variant) {
      if (!variant.available) return;
      var units = unitsOf(variant);
      if (units <= currentUnits) return;
      if (cups % units !== 0) return;
      if (variant.price / units >= current.price / currentUnits) return;
      if (!best || units > unitsOf(best)) best = variant;
    });

    return best;
  }

  function packNote(root) {
    var note = root.querySelector(".pdp__packnote");
    if (note) return note;

    var pills = root.querySelector(".pdp__pills");
    if (!pills) return null;

    note = document.createElement("p");
    note.className = "pdp__packnote";
    note.setAttribute("role", "status");
    note.hidden = true;
    pills.insertAdjacentElement("afterend", note);
    return note;
  }

  function showPackNote(root, text) {
    var note = packNote(root);
    if (!note) return;
    if (!text) { note.hidden = true; return; }
    note.textContent = text;
    note.hidden = false;
  }

  // ---- painting: the single place that turns "a variant" into DOM state,
  // called both on init and on every pack-size change so the two can never
  // drift out of sync. ----------------------------------------------------

  function syncPrice(root, variant) {
    var price = root.querySelector("#pdpPrice");
    if (!price) return;

    var now = price.querySelector(".price__now");
    if (now) now.textContent = money(variant.price);

    var was = price.querySelector(".price--was");
    var showWas = !!variant.compareAtPrice && variant.compareAtPrice > variant.price;
    if (showWas) {
      if (!was) {
        was = document.createElement("span");
        was.className = "price--was";
        price.appendChild(was);
      }
      was.textContent = money(variant.compareAtPrice);
      was.hidden = false;
    } else if (was) {
      was.hidden = true;
    }
  }

  function syncAddState(root, variant) {
    var buttons = root.querySelectorAll('#pdpBuyForm [type="submit"], .pdp__sticky [type="submit"]');
    // A flavour-picker product (see wireFlavors below) gates "Add to cart" on
    // its own running total in addition to plain availability — sold-out
    // wins the label, an incomplete flavour split just disables silently
    // (the flavour total line under the picker already explains why).
    var flavorsInvalid = root.dataset.flavorsInvalid === "true";
    buttons.forEach(function (btn) {
      btn.disabled = !variant.available || flavorsInvalid;
      btn.textContent = variant.available ? "Add to cart" : "Sold out";
    });
  }

  function syncVariantInput(root, variant) {
    var input = root.querySelector('#pdpBuyForm [name="variant_id"]');
    if (input) input.value = variant.id;
  }

  function syncStickyImage(root) {
    var sticky = root.querySelector(".pdp__sticky");
    var mainImg = root.querySelector(".pdp__main img");
    if (!sticky || !mainImg) return;
    var img = sticky.querySelector("img");
    if (img) { img.src = mainImg.src; img.alt = mainImg.alt; }
  }

  function syncSticky(root, variant) {
    var sticky = root.querySelector(".pdp__sticky");
    if (!sticky) return;
    syncStickyImage(root);
    var titleEl = root.querySelector(".pdp__title");
    var name = sticky.querySelector(".pdp__sticky-name");
    var stickyPrice = sticky.querySelector(".pdp__sticky-price");
    if (name && titleEl) name.textContent = titleEl.textContent;
    if (stickyPrice) stickyPrice.textContent = money(variant.price);
  }

  function paint(root, variants, variantId) {
    var variant = findVariant(variants, variantId);
    if (!variant) return;
    syncPrice(root, variant);
    syncAddState(root, variant);
    syncVariantInput(root, variant);
    syncSticky(root, variant);
  }

  // ---- wiring -------------------------------------------------------------

  function wirePackRadios(root, variants) {
    root.addEventListener("change", function (e) {
      if (!e.target.matches('[name="pack"]')) return;
      // A deliberate pack choice starts its own count, and clears any note
      // left over from an automatic upgrade.
      var qtyInput = root.querySelector('#pdpBuyForm input[type="number"]');
      if (qtyInput) qtyInput.value = 1;
      showPackNote(root, "");
      paint(root, variants, e.target.getAttribute("data-variant-id"));
    });
  }

  // Called after every quantity change: if the cups now on the form add up to
  // a bigger pack exactly, switch to it and restate the quantity in packs.
  function upgradePack(root, variants, qtyInput) {
    var radio = checkedPack(root);
    if (!radio) return;

    var current = findVariant(variants, radio.getAttribute("data-variant-id"));
    if (!current) return;

    var qty = clampQty(parseInt(qtyInput.value, 10));
    var cups = unitsOf(current) * qty;
    var upgrade = betterPack(variants, current, cups);
    if (!upgrade) return;

    var newQty = cups / unitsOf(upgrade);
    var was = current.price * qty;
    var now = upgrade.price * newQty;

    var target = root.querySelector('[name="pack"][data-variant-id="' + upgrade.id + '"]');
    if (target) target.checked = true;
    qtyInput.value = newQty;
    paint(root, variants, upgrade.id);

    showPackNote(root, "Switched to the " + upgrade.option + " — same " + cups +
      " cups for " + money(now) + " instead of " + money(was) + ".");
  }

  // Show one gallery slide, and keep the thumbnail rail's selected state and
  // the sticky bar's thumbnail in step with it.
  //
  // Split out of wireGallery's click handler so the thumbnails are no longer
  // the only thing that can drive the gallery: the flavour picker's "Nutrition
  // facts" links call this with the same `src` as the matching panel, and the
  // rail highlights that thumb as though it had been clicked. Two controls,
  // one code path, so they cannot fall out of sync.
  function showSlide(root, src, alt) {
    var mainImg = root.querySelector(".pdp__main img");
    if (!mainImg || !src) return false;

    /* srcset WINS over src wherever both are set, so assigning src alone
       leaves a responsive <img> showing the picture it already had — it just
       re-picks a candidate from the stale list at whatever width now fits.
       On the theme that read as the gallery swapping to the wrong photo, or
       appearing to zoom, when a "Nutrition facts" link was pressed (client,
       9 Sep). The demo's <img> carries neither attribute so this never
       surfaced there; Shopify's image_tag emits both.

       Cleared rather than rebuilt: one flat 1200px src is what every caller
       already passes, and a candidate list regenerated here would have to
       track image_tag's widths to stay honest. */
    mainImg.removeAttribute("srcset");
    mainImg.removeAttribute("sizes");
    mainImg.src = src;
    if (alt !== null && alt !== undefined) mainImg.alt = alt;

    // Match on the src rather than on the element clicked, so a link that is
    // nowhere near the rail still selects the right thumb.
    var matched = false;
    root.querySelectorAll(".pdp__thumb").forEach(function (t) {
      var isTarget = t.getAttribute("data-full-src") === src;
      if (isTarget) { t.setAttribute("aria-current", "true"); matched = true; }
      else t.removeAttribute("aria-current");
    });

    syncStickyImage(root);
    return matched;
  }

  // Generic on purpose: with zero or one .pdp__thumb (every product today)
  // there's simply nothing to click, so this quietly does nothing.
  function wireGallery(root) {
    var thumbs = root.querySelector(".pdp__thumbs");
    var mainImg = root.querySelector(".pdp__main img");
    if (!thumbs || !mainImg) return;

    thumbs.addEventListener("click", function (e) {
      var thumb = e.target.closest(".pdp__thumb");
      if (!thumb) return;
      showSlide(root, thumb.getAttribute("data-full-src"), thumb.getAttribute("data-full-alt"));
    });
  }

  // "Nutrition facts" beside each flavour in the picker (client screenshot 2).
  //
  // The picker lists four flavours the customer is choosing between, and the
  // thing they most want at that moment — how the flavours actually differ —
  // is four slides away in the gallery with nothing pointing at it. These
  // links jump the gallery straight to that flavour's own panel.
  //
  // Delegated on the picker wrapper because sohi-product-data.js rebuilds its
  // innerHTML on every render; a handler bound to the links themselves would
  // die with them.
  function wireFlavorNutritionLinks(root) {
    var wrap = root.querySelector("#pdpFlavors");
    if (!wrap) return;

    wrap.addEventListener("click", function (e) {
      var link = e.target.closest("[data-nutrition-src]");
      if (!link) return;
      e.preventDefault();

      var src = link.getAttribute("data-nutrition-src");
      showSlide(root, src, link.getAttribute("data-nutrition-alt"));

      // Bring the gallery into view — the picker sits well below it on mobile,
      // so switching a slide the customer cannot see would look like nothing
      // happened. Honour a reduced-motion preference rather than animating.
      var main = root.querySelector(".pdp__main");
      if (main && main.scrollIntoView) {
        var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        main.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "center" });
      }
    });
  }

  function wireStepper(root, variants) {
    // Scoped to the buy form specifically, not root's first .stepper —
    // wireFlavors below adds its own .stepper--sm rows earlier in the DOM
    // (inside #pdpFlavors, which sits above #pdpBuyForm), and those belong
    // to wireFlavors, not this box-quantity control.
    var form = root.querySelector("#pdpBuyForm");
    var stepper = form ? form.querySelector(".stepper") : root.querySelector(".stepper");
    if (!stepper) return;
    var input = stepper.querySelector('input[type="number"]');
    var minus = stepper.querySelector("[data-qty-minus]");
    var plus = stepper.querySelector("[data-qty-plus]");
    if (!input) return;

    function syncDisabled() {
      if (minus) minus.disabled = clampQty(parseInt(input.value, 10)) <= 1;
    }
    // Going up can promote you to a bigger pack; going down never does, so
    // stepping back off an upgrade leaves you on the pack you're now holding.
    function set(n, allowUpgrade) {
      input.value = clampQty(n);
      // The note describes one specific swap, so it only survives as long as
      // the quantity that produced it — upgradePack re-adds it if it fires.
      showPackNote(root, "");
      if (allowUpgrade) upgradePack(root, variants, input);
      syncDisabled();
    }

    if (minus) minus.addEventListener("click", function () { set(parseInt(input.value, 10) - 1, false); });
    if (plus) plus.addEventListener("click", function () { set(parseInt(input.value, 10) + 1, true); });
    input.addEventListener("change", function () { set(parseInt(input.value, 10), true); });
    syncDisabled();
  }

  // ---- flavour picker -----------------------------------------------------
  // Reads data-flavor-count off #pdpBuyForm and one [data-flavor-input] per
  // row inside #pdpFlavors. Dormant on the four flavour boxes — they are
  // single-flavour ("they cant mix flavors in one box", client, 4 Sep) — but
  // LIVE again on the Best Value Bundle, whose picker counts whole boxes
  // (data-flavor-unit="boxes"): five rows, one per flavour box, gated at
  // exactly pickBoxes before Add to cart enables.

  function flavorTarget(root) {
    var form = root.querySelector("#pdpBuyForm");
    var raw = form ? parseInt(form.getAttribute("data-flavor-count"), 10) : NaN;
    return isNaN(raw) ? null : raw;
  }

  // What one picker row counts in — "cups" for the old mixed boxes, "boxes"
  // for the Best Value Bundle, whose rows are whole flavour boxes. Only
  // the running-total copy reads this; the arithmetic is unit-agnostic.
  function flavorUnit(root) {
    var form = root.querySelector("#pdpBuyForm");
    return (form && form.getAttribute("data-flavor-unit")) || "cups";
  }

  // Optional cap on how many DIFFERENT flavours may be chosen — the Power
  // Box's "mix & match two flavors" (client, 2 Sep). Absent on every other
  // picker, where any split across the four is fine. Null means no cap.
  function flavorMaxDistinct(root) {
    var form = root.querySelector("#pdpBuyForm");
    var raw = form ? parseInt(form.getAttribute("data-flavor-max-distinct"), 10) : NaN;
    return isNaN(raw) ? null : raw;
  }

  function countDistinct(inputs) {
    return inputs.reduce(function (n, input) {
      return n + (clampFlavorQty(parseInt(input.value, 10)) > 0 ? 1 : 0);
    }, 0);
  }

  function flavorInputs(root) {
    return Array.prototype.slice.call(root.querySelectorAll("#pdpFlavors [data-flavor-input]"));
  }

  function clampFlavorQty(n) {
    if (isNaN(n) || n < 0) n = 0;
    return Math.min(99, n);
  }

  // Read-only summary of the current picker state — wireBuyForm calls this
  // on submit, so the map it sends to the cart can never drift from what
  // #pdpFlavorTotal is telling the shopper on screen.
  function readFlavorMap(root) {
    var map = {};
    flavorInputs(root).forEach(function (input) {
      var qty = clampFlavorQty(parseInt(input.value, 10));
      if (qty > 0) map[input.getAttribute("data-flavor-input")] = qty;
    });
    return map;
  }

  function flavorSum(inputs) {
    return inputs.reduce(function (sum, input) {
      return sum + clampFlavorQty(parseInt(input.value, 10));
    }, 0);
  }

  function wireFlavors(root, variants) {
    var target = flavorTarget(root);
    if (target === null) return; // not a flavour-picker product

    var inputs = flavorInputs(root);
    var totalEl = root.querySelector("#pdpFlavorTotal");
    var maxDistinct = flavorMaxDistinct(root);

    function sync() {
      var sum = flavorSum(inputs);
      var distinct = countDistinct(inputs);
      // Two independent gates: the cup total must hit the target, AND the
      // number of different flavours must not exceed the cap (where one is
      // set). Both have to pass before Add to cart enables.
      var tooManyFlavors = maxDistinct !== null && distinct > maxDistinct;
      var complete = sum === target && !tooManyFlavors;

      if (totalEl) {
        var msg;
        if (tooManyFlavors) {
          // The flavour cap is the more specific problem, so it wins the
          // message — telling someone "add 2 more" while they have three
          // flavours selected would send them the wrong way.
          msg = "Pick just " + maxDistinct + " flavors — you've chosen " + distinct;
        } else {
          msg = sum + " / " + target + " " + flavorUnit(root) + " selected" +
            (sum === target ? "" : sum > target
              ? " — over by " + (sum - target)
              : " — add " + (target - sum) + " more");
        }
        totalEl.textContent = msg;
        totalEl.classList.toggle("pdp__flavor-total--ok", complete);
        totalEl.classList.toggle("pdp__flavor-total--over", sum > target || tooManyFlavors);
      }

      // On Shopify (sections/sohi-product.liquid) each flavour row has a
      // matching hidden properties[Name] input — this is what actually
      // reaches Shopify's native cart on submit. Absent in the static demo
      // (product.html has none), where the flavour map instead rides
      // through window.SohiCart.addItem's 4th arg — see wireBuyForm.
      inputs.forEach(function (input) {
        var slug = input.getAttribute("data-flavor-input");
        var prop = root.querySelector('[data-flavor-property="' + slug + '"]');
        if (prop) prop.value = clampFlavorQty(parseInt(input.value, 10)) || "";
      });

      root.dataset.flavorsInvalid = complete ? "false" : "true";

      // Re-run the same paint the pack-radio path uses, so a flavour edit
      // disables/relabels "Add to cart" exactly like a sold-out pack would —
      // one gate, not two independent ones that could disagree.
      var radio = checkedPack(root);
      if (radio) {
        var variant = findVariant(variants, radio.getAttribute("data-variant-id"));
        if (variant) syncAddState(root, variant);
      }
    }

    inputs.forEach(function (input) {
      var row = input.closest("[data-flavor-row]");
      var minus = row ? row.querySelector("[data-flavor-minus]") : null;
      var plus = row ? row.querySelector("[data-flavor-plus]") : null;

      function set(n) {
        input.value = clampFlavorQty(n);
        sync();
      }

      if (minus) minus.addEventListener("click", function () { set(parseInt(input.value, 10) - 1); });
      if (plus) plus.addEventListener("click", function () { set(parseInt(input.value, 10) + 1); });
      input.addEventListener("change", function () { set(parseInt(input.value, 10)); });
    });

    sync();
  }

  function wireBuyForm(root) {
    var form = root.querySelector("#pdpBuyForm");
    if (!form) return;

    form.addEventListener("submit", function (e) {
      // ⚠️ Order matters here, and getting it wrong is what broke add-to-cart
      // on Shopify for the whole of the theme's life before 2 Sep.
      //
      // This used to preventDefault() unconditionally and only then check for
      // window.SohiCart. On Shopify that object does not exist — it is the
      // static demo's localStorage cart engine, excluded by .shopifyignore —
      // so every submit was cancelled and nothing was ever added to the cart.
      //
      // Bailing BEFORE preventDefault hands the event back untouched, and
      // snippets/sohi-cart-ajax.liquid (which delegates on document, so it
      // runs after this listener) takes it from there: POST to the Cart API,
      // re-render the drawer, open it. With JS off entirely the form posts
      // natively to /cart/add. Three layers, none of which need this file.
      if (!window.SohiCart) return;

      e.preventDefault();
      if (root.dataset.flavorsInvalid === "true") return; // button is disabled too; belt and suspenders

      var slug = form.getAttribute("data-product-slug");
      var variantInput = form.querySelector('[name="variant_id"]');
      var variantId = variantInput ? variantInput.value : "";
      if (!slug || !variantId) return;

      // A flavour-picker box is always exactly one box — the picker's own
      // total is what varies, not how many boxes go in the cart — so it
      // skips the plain qty stepper (hidden for these products anyway) and
      // rides its flavour breakdown along as a 4th arg instead.
      var target = flavorTarget(root);
      if (target !== null) {
        window.SohiCart.addItem(slug, variantId, 1, readFlavorMap(root));
        window.SohiCart.open();
        return;
      }

      var qtyInput = form.querySelector('input[type="number"]');
      var qty = qtyInput ? clampQty(parseInt(qtyInput.value, 10)) : 1;
      window.SohiCart.addItem(slug, variantId, qty);
      window.SohiCart.open();
    });
  }

  function wireSticky(root) {
    var buybox = root.querySelector(".pdp__buybox");
    var sticky = root.querySelector(".pdp__sticky");
    if (!buybox || !sticky) return;

    // The bar stands in for the buy button once you have scrolled PAST it —
    // so "not intersecting" is not on its own a reason to show it. A buybox
    // still below the fold is equally non-intersecting, and treating that as
    // a cue put the bar on screen at page load, then took it away as the real
    // button scrolled into view, then brought it back afterwards: it read as
    // the add-to-cart bar randomly disappearing mid-scroll.
    // boundingClientRect vs rootBounds says which side it went out on.
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var rootTop = entry.rootBounds ? entry.rootBounds.top : 0;
        var scrolledPast = entry.boundingClientRect.bottom <= rootTop;
        if (!entry.isIntersecting && scrolledPast) sticky.setAttribute("data-visible", "");
        else sticky.removeAttribute("data-visible");
      });
    }, { rootMargin: "-120px 0px 0px 0px" });

    observer.observe(buybox);
  }

  function init(root) {
    if (root.dataset.sohiReady === "true") return;
    root.dataset.sohiReady = "true";

    var variants = readVariants(root);
    wirePackRadios(root, variants);
    wireGallery(root);
    wireFlavorNutritionLinks(root);
    wireStepper(root, variants);
    wireFlavors(root, variants);
    wireBuyForm(root);
    wireSticky(root);

    // Always compute the initial price/compare/button state from the
    // checked radio ourselves — never assume the server or the hydrator
    // already painted it — so behaviour is identical however the page
    // got its initial state.
    var radio = checkedPack(root);
    if (radio) paint(root, variants, radio.getAttribute("data-variant-id"));
  }

  function boot(root) {
    (root || document).querySelectorAll("[data-product-detail]").forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { boot(); });
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", function (e) { boot(e.target); });
})();
